import React from 'react';
import { Button, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CheckOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SaveOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { FooterButton } from '../types/fieldTypes';

interface DynamicFooterProps {
  buttons: FooterButton[];
  onAction: (action: string, customAction?: string) => void;
  loading?: boolean;
}

export const DynamicFooter: React.FC<DynamicFooterProps> = ({
  buttons,
  onAction,
  loading,
}) => {
  const { t } = useTranslation();
  const leftButtons = buttons.filter(btn => btn.position === 'left');
  const rightButtons = buttons.filter(btn => btn.position === 'right');

  const getButtonType = (
    variant?: string
  ): 'primary' | 'default' | 'dashed' | 'link' | 'text' => {
    switch (variant) {
      case 'primary':
        return 'primary';
      case 'secondary':
        return 'default';
      default:
        return 'primary';
    }
  };

  const getButtonDanger = (variant?: string): boolean => {
    return variant === 'danger';
  };

  const getIcon = (iconName?: string) => {
    if (!iconName) return null;

    // Map Bootstrap icons to Ant Design icons
    const iconMap: Record<string, React.ReactNode> = {
      'bi-check': <CheckOutlined />,
      'bi-arrow-left': <ArrowLeftOutlined />,
      'bi-arrow-right': <ArrowRightOutlined />,
      'bi-save': <SaveOutlined />,
      'bi-trash': <DeleteOutlined />,
      'bi-exclamation-circle': <ExclamationCircleOutlined />,
    };

    return iconMap[iconName] || null;
  };

  const renderButton = (button: FooterButton) => {
    const isSubmit = button.action === 'submit';
    const icon = getIcon(button.icon);

    return (
      <Button
        key={button.label}
        type={getButtonType(button.variant)}
        danger={getButtonDanger(button.variant)}
        htmlType={isSubmit ? 'submit' : 'button'}
        onClick={() =>
          !isSubmit && onAction(button.action, button.customAction)
        }
        loading={loading && isSubmit}
        disabled={loading}
        size="large"
        style={{ flex: 1, minWidth: 120 }}
        icon={button.position === 'left' ? icon : undefined}
      >
        {loading && isSubmit ? t('dynamic_footer.submitting') : button.label}
        {button.position === 'right' && icon && (
          <span style={{ marginLeft: 8 }}>{icon}</span>
        )}
      </Button>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        marginTop: 24,
        justifyContent: 'space-between',
      }}
    >
      <Space size="middle" style={{ flex: 1 }}>
        {leftButtons.map(renderButton)}
      </Space>
      <Space size="middle" style={{ flex: 1, justifyContent: 'flex-end' }}>
        {rightButtons.map(renderButton)}
      </Space>
    </div>
  );
};
